import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, Protected } from "@/context/AuthContext";
import PublicLayout from "@/components/PublicLayout";
import DashboardLayout from "@/components/DashboardLayout";
import Home from "@/pages/public/Home";
import { About, FindTutorsPage } from "@/pages/public/About";
import Login from "@/pages/public/Login";
import Register from "@/pages/public/Register";
import { ForgotPassword, ResetPassword, VerifyEmail, ChangePassword } from "@/pages/public/AuthMisc";
import TutorProfile from "@/pages/TutorProfile";
import ProfileSettings from "@/pages/ProfileSettings";
import UsersPage from "@/pages/admin/UsersPage";
import { AdminDashboard, SessionsAdmin } from "@/pages/admin/AdminPages";
import { CreditsPage, AdminSettings } from "@/pages/admin/CreditsSettings";
import { StudentDashboard, StudentFindTutor, StudentRequests, StudentSessions, StudentWallet } from "@/pages/student/StudentPages";
import { TutorDashboard, TutorRequests, ApprovedStudents } from "@/pages/tutor/TutorPages";
import { TutorSessions } from "@/pages/tutor/TutorSessions";
import ScheduleSession from "@/pages/tutor/ScheduleSession";

const guard = (role) => <Protected role={role}><DashboardLayout role={role} /></Protected>;

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/tutors" element={<FindTutorsPage />} />
            <Route path="/tutors/:id" element={<div className="px-4 sm:px-6"><TutorProfile backTo="/tutors" /></div>} />
          </Route>
          <Route path="/login" element={<Navigate to="/login/student" replace />} />
          <Route path="/login/:role" element={<Login />} />
          <Route path="/admin/login" element={<Login portal="admin" />} />
          <Route path="/register" element={<Navigate to="/register/student" replace />} />
          <Route path="/register/:role" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/admin/change-password" element={<Protected role="admin"><ChangePassword /></Protected>} />

          <Route path="/admin" element={guard("admin")}>
            <Route index element={<AdminDashboard />} />
            <Route path="tutors" element={<UsersPage role="tutor" />} />
            <Route path="tutor-approvals" element={<UsersPage role="tutor" approvals />} />
            <Route path="students" element={<UsersPage role="student" />} />
            <Route path="student-approvals" element={<UsersPage role="student" approvals />} />
            <Route path="credits" element={<CreditsPage />} />
            <Route path="meetings" element={<SessionsAdmin scope="upcoming" />} />
            <Route path="history" element={<SessionsAdmin scope="history" />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          <Route path="/student" element={guard("student")}>
            <Route index element={<StudentDashboard />} />
            <Route path="find-tutor" element={<StudentFindTutor />} />
            <Route path="tutors/:id" element={<TutorProfile backTo="/student/find-tutor" />} />
            <Route path="requests" element={<StudentRequests />} />
            <Route path="sessions" element={<StudentSessions />} />
            <Route path="wallet" element={<StudentWallet />} />
            <Route path="profile" element={<ProfileSettings />} />
          </Route>
          <Route path="/tutor" element={guard("tutor")}>
            <Route index element={<TutorDashboard />} />
            <Route path="requests" element={<TutorRequests />} />
            <Route path="students" element={<ApprovedStudents />} />
            <Route path="schedule" element={<ScheduleSession />} />
            <Route path="upcoming" element={<TutorSessions scope="upcoming" />} />
            <Route path="history" element={<TutorSessions scope="history" />} />
            <Route path="profile" element={<ProfileSettings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
